import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiPhone, FiMail, FiMapPin, FiMessageCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    checkIn: '',
    checkOut: ''
  });

  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const contactInfo = [
    {
      icon: FiPhone,
      title: "Direct Line",
      detail: "+94 77 123 4567",
      description: "Available 24/7 for your convenience"
    },
    {
      icon: FiMail,
      title: "Email Reservations",
      detail: "stay@thalagaha.lk",
      description: "We respond within 2 hours"
    },
    {
      icon: FiMapPin,
      title: "Location",
      detail: "Kolonna, Ratnapura District",
      description: "3.5 hours from Colombo"
    },
    {
      icon: FiMessageCircle,
      title: "WhatsApp",
      detail: "+94 77 123 4567",
      description: "Instant booking assistance"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 }
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-forest-green to-forest-green/80 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white/30 rounded-full"
            initial={{ 
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight 
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.3, 1, 0.3]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={itemVariants}
            className="text-4xl md:text-5xl font-playfair font-bold text-white mb-6"
          >
            Begin Your Journey
          </motion.h2>
          
          <motion.div 
            variants={itemVariants}
            className="w-24 h-1 bg-terracotta-earth mx-auto mb-8"
          ></motion.div>
          
          <motion.p 
            variants={itemVariants}
            className="text-xl font-lato text-white/90 max-w-3xl mx-auto leading-relaxed"
          >
            Ready to experience the sanctuary? Contact us to craft your perfect retreat 
            in the heart of Sri Lanka's natural paradise.
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <motion.div
            variants={containerVariants}
            className="space-y-8"
          >
            <motion.h3 
              variants={itemVariants}
              className="text-2xl font-playfair font-semibold text-white mb-8"
            >
              Get in Touch
            </motion.h3>

            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ x: 10 }}
                className="flex items-start space-x-4 glass-card p-6"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-terracotta-earth/20 flex items-center justify-center">
                    <SafeIcon 
                      icon={info.icon} 
                      className="w-6 h-6 text-terracotta-earth" 
                    />
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-playfair font-semibold text-white mb-1">
                    {info.title}
                  </h4>
                  <p className="text-white/90 font-lato font-medium mb-1">
                    {info.detail}
                  </p>
                  <p className="text-white/70 font-lato text-sm">
                    {info.description}
                  </p>
                </div>
              </motion.div>
            ))}

            <motion.div
              variants={itemVariants}
              className="glass-card p-6 text-center"
            >
              <h4 className="text-lg font-playfair font-semibold text-white mb-4">
                Best Rate Guaranteed
              </h4>
              <p className="text-white/80 font-lato text-sm mb-4">
                Book direct and save 10% compared to third-party platforms. 
                Plus enjoy exclusive villa perks.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="glass-button-primary px-6 py-3 text-white font-lato font-medium"
              >
                Check Availability
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            variants={containerVariants}
            className="glass-card p-8"
          >
            <motion.h3 
              variants={itemVariants}
              className="text-2xl font-playfair font-semibold text-white mb-8"
            >
              Reservation Inquiry
            </motion.h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <motion.div variants={itemVariants} className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white/80 font-lato text-sm mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-terracotta-earth transition-colors"
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white/80 font-lato text-sm mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-terracotta-earth transition-colors"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white/80 font-lato text-sm mb-2">
                    Check-in Date
                  </label>
                  <input
                    type="date"
                    name="checkIn"
                    value={formData.checkIn}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-terracotta-earth transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-white/80 font-lato text-sm mb-2">
                    Check-out Date
                  </label>
                  <input
                    type="date"
                    name="checkOut"
                    value={formData.checkOut}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-terracotta-earth transition-colors"
                  />
                </div>
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-white/80 font-lato text-sm mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-terracotta-earth transition-colors"
                  placeholder="+94 77 123 4567"
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-white/80 font-lato text-sm mb-2">
                  Special Requests
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-terracotta-earth transition-colors resize-none"
                  placeholder="Tell us about your ideal sanctuary experience..."
                ></textarea>
              </motion.div>

              <motion.button
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="w-full glass-button-primary py-4 text-white font-lato font-medium text-lg"
              >
                Send Inquiry
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;